import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { AuthService } from '@/core/auth/auth.service';
import { CertificadoRegularidadeService } from '@/core/api/sicom-api/registro/medico/relatorios/certificado-regularidade/certificado-regularidade.service';
import { ReportCertificadoRegularidadeRequest, ReportCertificadoRegularidadeResponse, ReportJobCreatedResponse } from '@/core/api/sicom-api/registro/medico/relatorios/certificado-regularidade/certificado-regularidade.model';
import { CorrespondenciaService } from '@/core/api/sicom-api/registro/medico/relatorios/correspondencia/correspondencia.service';
import { CorrespondenciaRequest } from '@/core/api/sicom-api/registro/medico/relatorios/correspondencia/correspondencia.model';
import { CertificadoRegularidadeJobService } from '@/core/services/certificado-regularidade-job.service';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { SolicitanteSearchResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportCertificadoRegularidadeRequest;
type Resp = ReportCertificadoRegularidadeResponse;

/** Controles tipados do formulário do certificado (assinante + observação obrigatória). */
interface CertificadoRegularidadeControls {
    txObservacao: FormControl<string>;
    idAssinante: FormControl<number | null>;
    assinanteObj: FormControl<SolicitanteSearchResponse | null>;
}

@Component({
    selector: 'app-certificado-regularidade',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, TextareaModule, SelectEstado, RelatorioContainer, SelectModule, TooltipModule, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './certificado-regularidade.html'
})
/**
 * Relatório PDF assíncrono de certificado de regularidade.
 * Herda `RelatorioMedicoBase`: o fluxo registra a correspondência e, em seguida, exporta o PDF
 * (encadeados em `exportar()`); polling/download/erro e o card "médico selecionado + Alterar"
 * vêm da base.
 */
export class CertificadoRegularidadeComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly formBuilder = inject(FormBuilder);
    private readonly certificadoService = inject(CertificadoRegularidadeService);
    private readonly correspondenciaService = inject(CorrespondenciaService);
    private readonly usuarioService = inject(UsuarioService);
    private readonly authService = inject(AuthService);
    readonly certificadoJobService = inject(CertificadoRegularidadeJobService);

    readonly job$ = this.certificadoJobService.job$;

    form: FormGroup<CertificadoRegularidadeControls> = this.formBuilder.group<CertificadoRegularidadeControls>({
        txObservacao: new FormControl('', { nonNullable: true, validators: Validators.required }),
        idAssinante: new FormControl<number | null>(null, Validators.required),
        assinanteObj: new FormControl<SolicitanteSearchResponse | null>(null, Validators.required)
    });

    ufAssinante = '';
    ufAssinanteTocada = false;
    ufDestino = '';
    ufDestinoTocada = false;

    readonly assinantes = signal<SolicitanteSearchResponse[]>([]);
    readonly loadingAssinantes = signal(false);
    private usuarioLogadoId = '';
    private usuarioNome = '';
    private usuarioCpf = '';

    protected get jobFacade(): RelatorioJobFacade {
        return this.certificadoJobService;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.inicializarUsuarioLogado();
    }

    private inicializarUsuarioLogado(): void {
        from(this.authService.getUserInfo()).subscribe({
            next: (userInfo) => {
                this.usuarioLogadoId = userInfo.userId;
                this.usuarioCpf = userInfo.cpf || '';
                this.usuarioNome = userInfo.name || '';
                if (this.usuarioCpf) {
                    this.buscarAssinantePorUf('');
                }
            },
            // Toast local NECESSÁRIO: `getUserInfo()` lê claims do token e não passa por HttpClient,
            // então o interceptor global não vê esta falha. Sem isto, o erro seria silencioso.
            error: () =>
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro de autenticação',
                    detail: 'Não foi possível obter os dados do usuário logado.'
                })
        });
    }

    private buscarAssinantePorUf(uf: string): void {
        if (!this.usuarioCpf) return;

        this.loadingAssinantes.set(true);
        this.usuarioService.buscarSolicitante(this.usuarioCpf, uf, 0, 10).subscribe({
            next: (page) => {
                this.assinantes.set(page.content);
                if (page.content.length === 1) {
                    const solicitante = page.content[0];
                    this.ufAssinante = solicitante.uf;
                    this.form.patchValue({ assinanteObj: solicitante, idAssinante: solicitante.idSolicitante });
                }
                this.loadingAssinantes.set(false);
            },
            error: () => this.loadingAssinantes.set(false)
        });
    }

    onAssinanteChange(assinante: SolicitanteSearchResponse | null): void {
        this.ufAssinante = assinante?.uf ?? this.ufAssinante;
        this.form.patchValue({ idAssinante: assinante?.idSolicitante ?? null });
    }

    alterarUfAssinante(uf: string | null): void {
        this.ufAssinante = uf ?? '';
        this.form.patchValue({ assinanteObj: null, idAssinante: null });
        this.assinantes.set([]);
        if (this.ufAssinante) {
            this.buscarAssinantePorUf(this.ufAssinante);
        }
    }

    marcarUfAssinanteTocada(): void {
        this.ufAssinanteTocada = true;
    }

    marcarUfDestinoTocada(): void {
        this.ufDestinoTocada = true;
    }

    /** Monta o request do certificado; retorna null (abortando) se o form ou as UFs estiverem inválidos. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        this.ufAssinanteTocada = true;
        this.ufDestinoTocada = true;
        if (this.form.invalid || !this.ufAssinante || !this.ufDestino) return null;

        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar o certificado.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        const { txObservacao, idAssinante, assinanteObj } = this.form.getRawValue();

        return {
            nuCrm,
            sgUf,
            idCorrespondencia: 0,
            numeroCorrespondencia: '0',
            tipoSolicitacao: 'Certificado',
            tipoLiberacao: 'Web',
            sgUfDestino: this.ufDestino,
            txObservacao: txObservacao ?? '',
            idAssinante: idAssinante ?? 0,
            tipoCorrespondencia: 'Certificado de Regularidade',
            assinante: assinanteObj?.nmSolicitante ?? '',
            emissor: this.usuarioNome || (this.authService.identityClaims?.['preferred_username'] as string) || 'Sistema',
            inSexoEmissor: 'M'
        };
    }

    /** Registra a correspondência e, com o número gerado, exporta o PDF do certificado. */
    protected exportar(request: Req): Observable<HttpResponse<Resp | ReportJobCreatedResponse>> {
        const corrRequest: CorrespondenciaRequest = {
            txObservacao: request.txObservacao,
            ufAssinante: this.ufAssinante,
            idAssinante: request.idAssinante,
            dtValidade: this.formatarDataParaBackend(new Date()) ?? '',
            ufDestino: this.ufDestino,
            idEstruturaOrganizacional: 0
        };
        return this.correspondenciaService.gerarCorrespondencia(corrRequest, this.usuarioLogadoId).pipe(
            switchMap((corr) => {
                request.idCorrespondencia = corr.nuCorrespondencia;
                request.numeroCorrespondencia = `${corr.nuCorrespondencia}/${corr.nuAnoCorrespondencia}`;
                return this.certificadoService.exportarPdf(request);
            })
        );
    }

    limparFormulario(): void {
        const assinante = this.form.getRawValue().assinanteObj;
        this.form.reset({ txObservacao: '', idAssinante: null, assinanteObj: null });
        if (assinante) {
            this.form.patchValue({ assinanteObj: assinante, idAssinante: assinante.idSolicitante });
        }
        this.ufAssinanteTocada = false;
        this.ufDestino = '';
        this.ufDestinoTocada = false;
        this.limparJob();
    }
}
